import { createApi } from "@reduxjs/toolkit/query/react"
import { baseQuery } from "../baseQuerySl2"

/**
 * Read-only client for the SL1-import console (LOCKER-382 child D).
 *
 * There is no mutation endpoint here and none on the server: a recon and an
 * import are both operator-run from a shell, and the console's whole job is to
 * make the resulting estate legible. A button on this page that started a run
 * would be a fifth way to reach production data — see `Ap_SaGetSl1ImportJobs`.
 *
 * **There is also no report endpoint yet, and that is child C.** The report body
 * lives in a local file on the operator's machine (`sl1ReconSink.ts`), so the
 * detail screen loads it from disk rather than fetching it. When Decision 3 lands
 * this file gains ONE lazy query, shaped exactly like
 * `reservationSyncApi.getReservationSyncObjectUrl` — a presigned URL with
 * `keepUnusedDataFor: 0` — and `useSl1ReportSource` swaps its source. Nothing
 * else on the screen moves.
 */

/** Mirrors `Sl1ImportJobAttrs`. Kept structural — the console never writes one. */
export type Sl1ImportJobDto = {
  id: string
  jobId: string
  tenantId: string
  providerTenantId: string
  sl1TenantId?: string
  kind: "recon" | "apply"
  status:
    | "PENDING"
    | "RUNNING"
    | "COMPLETED"
    | "NEEDS_REVIEW"
    | "BLOCKED"
    | "PARTIAL"
    | "FAILED"
    | "SUPERSEDED"
  window: { fromDate: string | null; toDate: string | null }
  counts: {
    found: number
    matchedExact: number
    matchedFallback: number
    unmatched: number
    replacesHusk: number
    review: number
    blocked: number
    /**
     * Rows written. Present on APPLY records only — a recon writes no
     * reservations, and an apply classifies nothing, so the other counts are
     * zero on its record. Kept as a read fallback for apply runs made before
     * `appliedCount` was written (LOCKER-382).
     */
    imported?: number
  }
  anomalyCounts: Record<string, number>
  planDigest?: string
  basedOnJobId?: string
  appliedCursor?: number
  appliedCount?: number
  reportKey?: string
  reportBytes?: number
  /** `true` while the report is a file on the operator's disk (child C is open). */
  reportIsLocal?: boolean
  /** The run's manifest — every stored object, across every kind. */
  objects?: {
    kind: "report" | "findings" | "snapshot"
    key: string
    bytes: number
    count: number
    rowFrom: number
    rowTo: number
    sha256: string
  }[]
  bucket?: string
  schemaVersion: number
  scriptVersion: string
  gitCommit: string
  startedAt: string
  finishedAt?: string
  triggeredBy?: string
  error?: string
}

/** Mirrors `Sl1ImportFleetAttrs`. */
export type Sl1ImportFleetDto = {
  id: string
  sl1TenantId: string
  /**
   * NULL is a legitimate reading, not a load failure. The link exists —
   * `Tenant.mblsProviderId` — but it is unset on some tenants, and a provider id
   * can be claimed by two of them (ticket `N1`, `N7`). Either way it renders as
   * `Needs mapping`, and the UI must never fall back to a guess.
   */
  sl2TenantId: string | null
  /**
   * `true` when 2+ SL2 tenants claim this provider's `mblsProviderId`.
   *
   * Distinct from a plain unmapped row and rendered as a marker, not a seventh
   * status: the two readings need different actions from the operator, but the
   * approved status vocabulary is six states (`N7`).
   */
  sl2TenantAmbiguous?: boolean
  /** The SL2 tenant ids claiming this provider id. Empty unless ambiguous. */
  sl2TenantCandidates?: string[]
  providerName: string
  vendor?: string
  sl1Rows: number
  matchedInSl2: number
  imported: number
  lastReconJobId?: string
  lastApplyJobId?: string
  lastJobStatus?: Sl1ImportJobDto["status"]
  lastJobKind?: "recon" | "apply"
  lastReconAt?: string
  lastApplyAt?: string
  sl1RowsAtLastRecon?: number
  /**
   * The window `matchedInSl2` was measured over. `sl1Rows` is all-time, so a
   * NARROWER window here means the "In SL2" figure would otherwise read as a
   * total it never was.
   *
   * Both ends `null` means the recon was all-time too, and the two numbers are
   * directly comparable — `sl1WindowScoped` is the check, never truthiness of the
   * object, which is true in both cases.
   */
  countsWindow?: { fromDate: string | null; toDate: string | null }
  scannedAt: string
}

type GetThingsSaResponse<T> = {
  things: T[]
  meta: { count: number; nextToken?: string }
}

export const sl1ImportApi = createApi({
  reducerPath: "sl1ImportApi",
  baseQuery,
  endpoints: (builder) => ({
    getSl1ImportFleet: builder.query<
      GetThingsSaResponse<Sl1ImportFleetDto>,
      { limit?: number } | void
    >({
      query: (args) => ({
        url: "things",
        params: {
          "ap.name": "SaGetSl1ImportFleet",
          ...(args?.limit ? { "ap.limit": args.limit } : {}),
        },
      }),
    }),

    getSl1ImportJobs: builder.query<
      GetThingsSaResponse<Sl1ImportJobDto>,
      { providerTenantId?: string; limit?: number } | void
    >({
      query: (args) => ({
        url: "things",
        params: {
          "ap.name": "SaGetSl1ImportJobs",
          ...(args?.limit ? { "ap.limit": args.limit } : {}),
          ...(args?.providerTenantId
            ? { "ap.providerTenantId": args.providerTenantId }
            : {}),
        },
      }),
    }),

    /**
     * A short-lived presigned URL for ONE stored object (LOCKER-382 child C).
     *
     * `keepUnusedDataFor: 0` — the URL expires in five minutes and a `snapshot`
     * part carries raw customer rows out of `mobilus2`. Caching it would hand the
     * next reader a stale but still-live token, and would also skip the
     * server-side audit entry that a snapshot read is supposed to write.
     */
    getSl1ImportObjectUrl: builder.query<
      {
        data: {
          url: string
          expiresInSeconds: number
          kind: string
          bytes: number
          count: number
          rowFrom: number
          rowTo: number
          sha256: string
        }
      },
      { jobId: string; key: string }
    >({
      query: ({ jobId, key }) => ({
        url: "sl1-imports/object",
        params: { jobId, key },
      }),
      keepUnusedDataFor: 0,
    }),
  }),
})

export const {
  useGetSl1ImportFleetQuery,
  useGetSl1ImportJobsQuery,
  useLazyGetSl1ImportObjectUrlQuery,
} = sl1ImportApi
